import { eventIsPrivateFor, eventUtcMs, formatTzTime, currentUser, fetchEventDetails } from '../../functions/calendar/calendarUtils.js'

// Team Now view (aligned to user's local time):
//
// One shared time axis on the left, in the USER's own timezone, covering
// ±12 hours from "now". Each visible team member is a column that just
// holds events + unavailable bands — both positioned by their UTC offset
// from the visible window's top, so they line up across columns and against
// the user's time gutter.
//
// A horizontal red NOW line is rendered at the center (UTC = Date.now()).
//
// Unavailable bands are derived per-member: at each minute of the visible
// window, look up that minute's local time in the member's tz and mark it
// "unavailable" if outside their working hours OR during an OOO date. The
// resulting contiguous unavailable ranges are rendered as `red.07` overlays.

const TOTAL_HOURS = 24
const HOUR_PX = 56
const HOUR_MS = 3600 * 1000
const TOTAL_PX = TOTAL_HOURS * HOUR_PX

// Anchor the visible window's top to the CURRENT round hour (UTC), so the
// view shows the next 24 hours starting from the current hour. The NOW
// indicator floats just below the top label by however many minutes have
// elapsed since the hour rolled over.
const topMsAtHourBoundary = () => {
  const nowMs = Date.now()
  return Math.floor(nowMs / HOUR_MS) * HOUR_MS
}

// Y position of the actual "now" inside the visible window — between 0 and
// HOUR_PX since the top is the current hour boundary.
const nowYPx = () => {
  const nowMs = Date.now()
  return ((nowMs - topMsAtHourBoundary()) / HOUR_MS) * HOUR_PX
}

// --- view ------------------------------------------------------------------

export const CalTeamNowView = {
  display: (el, s) => s.root.viewMode === 'team-now' ? 'flex' : 'none',
  flow: 'y',
  flex: '1',
  overflow: 'hidden',
  userSelect: 'none',

  Header: {
    flow: 'x',
    borderBottomWidth: '1px',
    borderBottomColor: 'line',
    borderBottomStyle: 'solid',
    minHeight: 'D1',

    // Spacer aligned with the time gutter below so member headers line up
    // with their columns.
    GutterSpacer: {
      width: 'E',
      minWidth: 'E',
      borderRightWidth: '1px',
      borderRightColor: 'line',
      borderRightStyle: 'solid'
    },

    Members: {
      flex: '1',
      flow: 'x',
      overflow: 'hidden',

      childExtends: 'CalTeamNowHeaderCell',
      childrenAs: 'state',
      children: (el, s) => {
        const all = el.call('activeMembers')
        const sel = s.root.teamMembers || []
        if (!sel.length) return all
        const visible = new Set(sel)
        return all.filter(p => visible.has(p.email))
      }
    }
  },

  ScrollBody: {
    flex: '1',
    overflow: 'auto',
    position: 'relative',
    flow: 'x',
    userSelect: 'none',
    alignItems: 'flex-start',

    onCreate: (el) => {
      requestAnimationFrame(() => {
        if (!el.node) return
        // Start at the top — the current hour is the first row.
        el.node.scrollTop = 0
      })
    },

    TimeGutter: {
      width: 'E',
      minWidth: 'E',
      borderRightWidth: '1px',
      borderRightColor: 'line',
      borderRightStyle: 'solid',
      flow: 'y',
      userSelect: 'none',
      position: 'relative',
      height: `${TOTAL_PX}px`,

      childExtends: 'CalTeamNowGutterTick',
      childrenAs: 'state',
      // 24 hourly tick labels showing the user's LOCAL time at each row.
      // Anchored to round-hour boundaries so labels read 08:00, 09:00, etc.
      children: (el, s) => {
        void s.root.currentMinutes
        const topMs = topMsAtHourBoundary()
        return Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => {
          const ms = topMs + i * HOUR_MS
          const label = new Date(ms).toLocaleTimeString(undefined, {
            hour: '2-digit', minute: '2-digit', hour12: false
          })
          return { idx: i, label }
        })
      }
    },

    // Wrap so the NOW line can be a sibling of Columns (childrenAs would
    // otherwise clobber any named child).
    ColumnsWrap: {
      flex: '1',
      position: 'relative',
      height: `${TOTAL_PX}px`,

      Columns: {
        flow: 'x',
        position: 'absolute',
        inset: '0',

        childExtends: 'CalTeamNowColumn',
        childrenAs: 'state',
        children: (el, s) => {
          void s.root.currentMinutes
          const all = el.call('activeMembers')
          const sel = s.root.teamMembers || []
          if (!sel.length) return all
          const visible = new Set(sel)
          return all.filter(p => visible.has(p.email))
        }
      },

      // Horizontal NOW line at the actual current minute. The grid rows
      // (hour rectangles) sit on round hour boundaries, but this line
      // floats freely between them showing the precise current moment.
      NowLineWrap: {
        position: 'absolute',
        left: '0',
        right: '0',
        height: '0',
        zIndex: 5,
        pointerEvents: 'none',
        top: (el, s) => {
          void s.root.currentMinutes
          return `${nowYPx()}px`
        },

        NowBar: {
          position: 'absolute',
          left: '0',
          right: '0',
          top: '0',
          height: '2px',
          transform: 'translateY(-50%)',
          background: 'red'
        },
        NowDot: {
          position: 'absolute',
          left: '0',
          top: '0',
          width: '12px',
          height: '12px',
          transform: 'translate(-50%, -50%)',
          round: '50%',
          background: 'red'
        }
      }
    }
  }
}

export const CalTeamNowGutterTick = {
  position: 'absolute',
  left: '0',
  right: '0',
  height: `${HOUR_PX}px`,
  padding: 'Y Z',
  fontSize: 'Y2',
  color: 'caption',
  fontFamily: 'monospace',
  align: 'flex-start right',
  paddingRight: 'Z1',
  borderTopWidth: '1px',
  borderTopColor: 'line',
  borderTopStyle: 'solid',
  top: (el, s) => `${s.idx * HOUR_PX}px`,
  text: (el, s) => s.label || ''
}

export const CalTeamNowHeaderCell = {
  flex: '1',
  flow: 'y',
  align: 'center center',
  gap: 'Y',
  padding: 'Y Z',
  borderRightWidth: '1px',
  borderRightColor: 'line',
  borderRightStyle: 'solid',
  minWidth: '0',
  overflow: 'hidden',

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B1 B1',
    src: (el, s) => el.call('avatarUrl', s.name)
  },
  Name: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '600',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    textAlign: 'center',
    text: (el, s) => (s.name || '').split(' ')[0]
  },
  LocalTime: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    fontFamily: 'monospace',
    text: (el, s) => {
      void el.state.root.currentMinutes
      return s.timezone ? formatTzTime(Date.now(), s.timezone) : ''
    }
  }
}

export const CalTeamNowColumn = {
  flex: '1',
  position: 'relative',
  borderRightWidth: '1px',
  borderRightColor: 'line',
  borderRightStyle: 'solid',
  minWidth: '0',
  height: `${TOTAL_PX}px`,

  // Hour grid lines so the column has visual rows even without inner cells.
  HourLines: {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',

    childExtends: 'CalTeamNowHourLine',
    childrenAs: 'state',
    children: () => Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => ({ idx: i }))
  },

  Unavailable: {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',

    childExtends: 'CalUnavailableSlot',
    childrenAs: 'state',
    children: (el, s) => {
      const root = el.state?.root
      // Touch reactive deps up-front so the signal graph subscribes.
      const events = root?.events || []
      const cm = root?.currentMinutes
      void cm
      const topMs = topMsAtHourBoundary()
      // Same per-column scoping as the Events layer below.
      const colEvents = events.filter(ev =>
        ev.googleCalendarId === s.email || (!ev.googleCalendarId && (ev.invitees || []).includes(s.email))
      )
      return el.call('getUnavailableRanges', s, topMs, TOTAL_HOURS * 60, 15, colEvents)
    }
  },

  Events: {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',

    childExtends: 'CalTeamNowEvent',
    childrenAs: 'state',
    children: (el, s) => {
      const root = el.state.root
      void root.currentMinutes
      const topMs = topMsAtHourBoundary()
      const bottomMs = topMs + TOTAL_HOURS * HOUR_MS
      const DAY_MS = 86400000
      return (root.events || [])
        .filter(ev =>
          (!ev.type || ev.type === 'meeting' || ev.type === 'general' || ev.type === 'busy') &&
          (ev.googleCalendarId === s.email || (!ev.googleCalendarId && (ev.invitees || []).includes(s.email)))
        )
        .map(ev => {
          const startMs = eventUtcMs(ev.date, ev.startTime)
          let endMs = eventUtcMs(ev.date, ev.endTime)
          // Cross-midnight: end <= start means the event wraps into the next
          // calendar day. Add 24h so the chip renders with positive height.
          if (endMs <= startMs) endMs += DAY_MS
          return { ...ev, _startMs: startMs, _endMs: endMs, _topMs: topMs }
        })
        .filter(ev => ev._endMs > topMs && ev._startMs < bottomMs)
    }
  }
}

export const CalTeamNowHourLine = {
  position: 'absolute',
  left: '0',
  right: '0',
  height: '0',
  borderTopWidth: '1px',
  borderTopColor: 'line',
  borderTopStyle: 'solid',
  top: (el, s) => `${s.idx * HOUR_PX}px`
}

export const CalTeamNowEvent = {
  tag: 'button',
  position: 'absolute',
  left: '2px',
  right: '2px',
  round: 'Z',
  padding: 'Y2 Z',
  border: 'none',
  cursor: 'pointer',
  overflow: 'hidden',
  color: 'white',
  fontSize: 'Y2',
  fontWeight: '500',
  pointerEvents: 'auto',
  flow: 'y',
  align: 'flex-start flex-start',
  gap: 'Y2',
  // Same color rules as CalWeekEvent: meeting=green, general=blue, busy=red,
  // private mask = green (so superadmin meetings look like normal meetings to
  // others without leaking details).
  background: (el, s) => {
    if (eventIsPrivateFor(s, currentUser(s.root))) return 'green'
    if (s.type === 'general') return 'blue'
    if (s.type === 'busy') return 'red'
    return 'green'
  },
  top: (el, s) => `${Math.max(0, (s._startMs - s._topMs) / 3600000 * HOUR_PX)}px`,
  height: (el, s) => {
    const start = Math.max(s._startMs, s._topMs)
    const end = Math.min(s._endMs, s._topMs + TOTAL_HOURS * 3600 * 1000)
    return `${Math.max(20, (end - start) / 3600000 * HOUR_PX)}px`
  },
  onClick: (e, el, s) => {
    const root = el.state.root
    root.update({ selectedEvent: s, showEventDetail: true })
    if (s.invitees === undefined || s.description === undefined) fetchEventDetails(s.id, root)
  },

  Title: {
    tag: 'span',
    fontWeight: '600',
    text: (el, s) => s.title || '',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    display: 'block'
  },
  Time: {
    tag: 'span',
    fontFamily: 'monospace',
    text: (el, s) => `${s.startTime} – ${s.endTime}`,
    opacity: '.85',
    display: 'block',
    width: '100%'
  }
}
